import React from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import Validator from '../../shared/validator';
import Loading from '../../shared/loading';
import parseFailXhr from '../../shared/parse-fail-xhr';
import { EMAIL_REGEX } from '../../shared/consts';
import mutate from '../../shared/mutate';
import ErrorBoundary from '../../shared/error-boundary';
import { contactAsync } from '../../services/contact-services';

export default class ContactForm extends React.Component {
  static propTypes = {
    hideSubject: PropTypes.bool,
    hideSubmit: PropTypes.bool,
    subject: PropTypes.string,
    message: PropTypes.string,
    onAfterSubmit: PropTypes.func,
  };

  static defaultProps = {
    hideSubject: false,
    hideSubmit: false,
    subject: '',
    message: '',
    onAfterSubmit: null,
  };

  constructor(props, context) {
    super(props, context);

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleEmailAddressChange = this.handleEmailAddressChange.bind(this);
    this.handleSubjectChange = this.handleSubjectChange.bind(this);
    this.handleMessageChange = this.handleMessageChange.bind(this);

    this.validator = new Validator({
      nameRequired: state => !!state.name,
      emailAddressRequired: state => !!state.emailAddress,
      emailAddressRegex: state => !state.emailAddress || EMAIL_REGEX.test(state.emailAddress),
      subjectRequired: state => !!state.subject,
      messageRequired: state => !!state.message,
    });
    this.state = {
      messageSent: false,
      name: '',
      emailAddress: '',
      subject: this.props.subject || '',
      message: this.props.message || '',
      validations: this.validator.getValidations(),
    };
  }

  handleNameChange(event) {
    const name = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.name = name;
      s.validations.nameRequiredValid = this.validator.validateNameRequired(s);
    }));
  }

  handleEmailAddressChange(event) {
    const emailAddress = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.emailAddress = emailAddress;
      s.validations.emailAddressRequiredValid = this.validator.validateEmailAddressRequired(s);
      s.validations.emailAddressRegexValid = this.validator.validateEmailAddressRegex(s);
    }));
  }

  handleSubjectChange(event) {
    const subject = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.subject = subject;
      s.validations.subjectRequiredValid = this.validator.validateSubjectRequired(s);
    }));
  }

  handleMessageChange(event) {
    const message = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.message = message;
      s.validations.messageRequiredValid = this.validator.validateMessageRequired(s);
    }));
  }

  clear() {
    this.validator.resetValidation();
    this.setState({
      messageSent: false,
      name: '',
      emailAddress: '',
      subject: this.props.subject || '',
      message: this.props.message || '',
      validations: this.validator.getValidations(),
    });
  }

  submit(event) {
    event && event.preventDefault && event.preventDefault();

    this.setState(
      state => ({
        validations: this.validator.validateForm(state),
      }),
      () => {
        if (!this.validator.formValid) return;

        this.loading.start();
        contactAsync({
          name: this.state.name,
          emailAddress: this.state.emailAddress,
          subject: this.state.subject,
          message: this.state.message,
        })
          .done(() => {
            this.validator.resetValidation();
            this.setState(s => mutate(s, ss => {
              ss.messageSent = true;
              ss.name = '';
              ss.emailAddress = '';
              ss.subject = '';
              ss.message = '';
              ss.validations = this.validator.getValidations();
            }));
            toastr.success('Your message has been sent');
            this.props.onAfterSubmit && this.props.onAfterSubmit();
          })
          .fail(xhr => {
            const errors = parseFailXhr(xhr);

            errors.anyUnhandledErrors() && toastr.error(errors.getErrorSummary(), 'Error');
          })
          .always(() => this.loading.stop());
      },
    );
  }

  renderName() {
    const isValid = this.state.validations.nameRequiredValid;
    return (
      <p className={`form-group ${isValid || 'has-error'}`}>
        <input
          type="text"
          placeholder="Name"
          value={this.state.name}
          onChange={this.handleNameChange}
        />
        {isValid || <span className="help-block">Name is required</span>}
      </p>
    );
  }

  renderEmail() {
    const { emailAddressRequiredValid, emailAddressRegexValid } = this.state.validations;
    return (
      <p className={`form-group ${emailAddressRequiredValid && emailAddressRegexValid || 'has-error'}`}>
        <input
          type="email"
          placeholder="Email"
          value={this.state.emailAddress}
          onChange={this.handleEmailAddressChange}
        />
        {emailAddressRequiredValid || <span className="help-block">Email is required</span>}
        {emailAddressRegexValid || <span className="help-block">Email is invalid</span>}
      </p>
    );
  }

  renderSubject() {
    const isValid = this.state.validations.subjectRequiredValid;
    return (
      <p className={`form-group ${isValid || 'has-error'}`}>
        <input
          type="text"
          placeholder="Subject"
          value={this.state.subject}
          onChange={this.handleSubjectChange}
        />
        {isValid || <span className="help-block">Subject is required</span>}
      </p>
    );
  }

  renderMessage() {
    const isValid = this.state.validations.messageRequiredValid;
    return (
      <p className={`form-group ${isValid || 'has-error'}`}>
        <textarea
          placeholder="Message"
          value={this.state.message}
          onChange={this.handleMessageChange}
        />
        {isValid || <span className="help-block">Message is required</span>}
      </p>
    );
  }

  render() {
    return (
      <ErrorBoundary>
        <div className="contact-form">
          <form onSubmit={e => this.submit(e)}>
            <Loading ref={el => (this.loading = el)} variant="small">
              {this.renderName()}
              {this.renderEmail()}
              {this.props.hideSubject || this.renderSubject()}
              {this.renderMessage()}
              {this.props.hideSubmit || (
                <button type="submit" className="btn btn-primary">Send</button>
              )}
            </Loading>
          </form>
          {!this.state.messageSent || (
            <div className="alert alert-success">
              <strong>Success!</strong>
              {' Your message has been sent to the team at Livewire.'}
            </div>
          )}
        </div>
      </ErrorBoundary>
    );
  }
}
